<template>
  <div class="raport-vanzari">

    <!-- Navigation Menu -->
    <v-card class="nav-menu-card" elevation="2">
      <div class="nav-menu">
        <router-link v-for="(item, index) in menuItems" :key="index" :to="item.route" class="nav-item"
          :class="{ 'active': isActiveRoute(item.route) }">
          <v-icon :color="isActiveRoute(item.route) ? APP_Color : '#718096'" size="20">{{ item.icon }}</v-icon>
          <span class="nav-label">{{ item.label }}</span>
        </router-link>
      </div>
    </v-card>

    <!-- Filtru perioadă -->
    <v-card class="filter-card mb-4" elevation="1">
      <div class="filter-row">
        <div class="filter-group">
          <v-icon size="18" class="mr-1" color="grey">mdi-calendar-month</v-icon>
          <span class="filter-label">Perioadă:</span>

          <v-select v-model="lunaSelectata" :items="luni" item-title="label" item-value="value"
            density="compact" variant="outlined" hide-details class="filter-select ml-2" />

          <v-select v-model="anSelectat" :items="ani" density="compact" variant="outlined" hide-details
            class="filter-select ml-2" />

          <v-btn :color="APP_Color" density="compact" class="ml-3 refresh-btn" :loading="loading"
            @click="incarcaRaport">
            <v-icon size="16" class="mr-1">mdi-refresh</v-icon> Actualizează
          </v-btn>
        </div>

        <div class="filter-group ml-4">
          <v-icon size="18" class="mr-1" color="grey">mdi-magnify</v-icon>
          <v-text-field v-model="searchAgent" placeholder="Caută agent..." density="compact"
            variant="outlined" hide-details clearable style="width: 320px; min-width: 320px;" />
        </div>
      </div>
    </v-card>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-center align-center py-8">
      <v-progress-circular indeterminate :color="APP_Color" size="48" />
    </div>

    <!-- Fără date -->
    <v-alert v-else-if="!loading && raportData.length === 0 && hasLoaded" type="info" variant="tonal" class="mb-4">
      <template #title>Nicio înregistrare</template>
      Nu există documente cu agent asociat pentru <strong>{{ lunaNume }} {{ anSelectat }}</strong>.
    </v-alert>

    <div v-else-if="!loading && raportData.length > 0">

      <!-- Sumar global -->
      <v-row class="mb-4" dense>
        <v-col cols="12" sm="3">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" style="background: rgba(33,150,243,0.1)">
                <v-icon color="#2196F3" size="24">mdi-account-group</v-icon>
              </div>
              <div>
                <div class="summary-value">{{ raportData.length }}</div>
                <div class="summary-label">Agenți activi</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" style="background: rgba(76,175,80,0.1)">
                <v-icon color="#4CAF50" size="24">mdi-cash-multiple</v-icon>
              </div>
              <div>
                <div class="summary-value summary-value--small">{{ formatValoare(totalCifra) }}</div>
                <div class="summary-label">Cifră totală</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" :style="{ background: `rgba(${marjaColorRGB(totalMarjaPct)}, 0.1)` }">
                <v-icon :color="getMarjaColor(totalMarjaPct)" size="24">mdi-trending-up</v-icon>
              </div>
              <div>
                <div class="summary-value summary-value--small" :style="{ color: getMarjaColor(totalMarjaPct) }">
                  {{ formatValoare(totalMarjaValoare) }}
                </div>
                <div class="summary-label">Marjă totală</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" :style="{ background: `rgba(${marjaColorRGB(totalMarjaPct)}, 0.1)` }">
                <v-icon :color="getMarjaColor(totalMarjaPct)" size="24">mdi-percent</v-icon>
              </div>
              <div>
                <div class="summary-value" :style="{ color: getMarjaColor(totalMarjaPct) }">
                  {{ totalMarjaPct.toFixed(1) }}%
                </div>
                <div class="summary-label">Marjă medie ponderată</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Agent Cards -->
      <v-row dense>
        <v-col v-for="agent in raportDataFiltrat" :key="agent.AgentId" cols="12" sm="6" md="4">
          <v-card class="agent-card" :class="{ 'agent-card--expanded': expandedId === agent.AgentId }" elevation="1">

            <!-- Header card -->
            <div class="agent-card-inner" @click="toggleDetalii(agent)" style="cursor:pointer">
              <div class="agent-header">
                <v-chip v-if="agent.AgentCod" size="x-small" color="blue-grey" variant="tonal" label>
                  {{ agent.AgentCod }}
                </v-chip>
                <v-chip size="x-small" variant="tonal" class="ml-1"
                  :color="agent.NrDocumente > 0 ? 'blue' : 'grey'">
                  {{ agent.NrDocumente }} doc.
                </v-chip>
                <!-- Badges clasificare -->
                <v-chip v-if="agent.FacturiCuAviz?.length" size="x-small" color="blue" variant="tonal" class="ml-1">
                  {{ agent.FacturiCuAviz.length }} aviz
                </v-chip>
                <v-chip v-if="agent.FacturiFaraAviz?.length" size="x-small" color="purple" variant="tonal" class="ml-1">
                  {{ agent.FacturiFaraAviz.length }} serv.
                </v-chip>
                <v-chip v-if="agent.FacturiStorno?.length" size="x-small" color="red" variant="tonal" class="ml-1">
                  {{ agent.FacturiStorno.length }} storno
                </v-chip>
                <v-icon class="ml-auto expand-icon"
                  :class="{ 'expand-icon--open': expandedId === agent.AgentId }"
                  size="18" color="grey">mdi-chevron-down</v-icon>
              </div>

              <div class="agent-name">{{ agent.AgentNume }}</div>

              <div class="agent-cifra-row">
                <span class="agent-cifra-val">{{ formatValoare(agent.Cifra) }}</span>
                <span class="agent-cifra-label">RON cifră</span>
              </div>

              <div class="agent-marja-row">
                <span class="agent-marja-label">Marjă:</span>
                <span class="agent-marja-val" :style="{ color: getMarjaColor(agent.MarjaProcentaj) }">
                  {{ formatValoare(agent.MarjaValoare) }} RON
                </span>
                <span class="agent-pct ml-auto" :style="{ color: getMarjaColor(agent.MarjaProcentaj) }">
                  {{ agent.MarjaProcentaj.toFixed(1) }}%
                </span>
              </div>
            </div>

            <!-- Expand: documente grupate pe categorii -->
            <v-expand-transition>
              <div v-if="expandedId === agent.AgentId" class="agent-detalii">

                <div v-if="!agent.FacturiCuAviz?.length && !agent.FacturiFaraAviz?.length && !agent.FacturiStorno?.length"
                  class="detalii-empty">
                  <v-icon size="20" color="grey-lighten-1">mdi-information-outline</v-icon>
                  <span class="ml-1">Nicio înregistrare găsită.</span>
                </div>

                <template v-else>

                  <!-- 1. Facturi cu aviz -->
                  <div v-if="agent.FacturiCuAviz?.length" class="doc-group">
                    <div class="doc-group-header doc-group-header--aviz">
                      <v-icon size="13" class="mr-1">mdi-truck-check</v-icon>
                      Facturi cu aviz
                      <span class="doc-group-count">{{ agent.FacturiCuAviz.length }}</span>
                    </div>
                    <div class="detalii-scroll">
                      <table class="detalii-table">
                        <thead>
                          <tr>
                            <th>Nr. Document</th>
                            <th>Data</th>
                            <th class="col-num">Cifră</th>
                            <th class="col-num">Marjă val.</th>
                            <th class="col-num">Marjă %</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="doc in agent.FacturiCuAviz" :key="doc.DocumentId">
                            <td class="col-numar">
                              {{ doc.Numar || '—' }}
                              <span v-if="doc.LivrataPartial" class="badge-partial">livrat parțial</span>
                              <span v-if="doc.AreLiniiStorno" class="badge-storno-linii">linii storno</span>
                            </td>
                            <td class="col-data">{{ formatData(doc.Data) }}</td>
                            <td class="col-num">{{ formatValoare(doc.Cifra) }}</td>
                            <td class="col-num" :style="{ color: getMarjaColor(doc.MarjaProcentaj) }">
                              {{ formatValoare(doc.MarjaValoare) }}
                            </td>
                            <td class="col-num">
                              <v-chip size="x-small" variant="tonal"
                                :color="doc.MarjaProcentaj >= 20 ? 'green' : doc.MarjaProcentaj >= 10 ? 'orange' : 'red'">
                                {{ doc.MarjaProcentaj.toFixed(1) }}%
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- 2. Facturi fără aviz (servicii + livrate parțial) -->
                  <div v-if="agent.FacturiFaraAviz?.length" class="doc-group">
                    <div class="doc-group-header doc-group-header--fara-aviz">
                      <v-icon size="13" class="mr-1">mdi-file-document-outline</v-icon>
                      Facturi fără aviz
                      <span class="doc-group-count">{{ agent.FacturiFaraAviz.length }}</span>
                    </div>
                    <div class="detalii-scroll">
                      <table class="detalii-table">
                        <thead>
                          <tr>
                            <th>Nr. Document</th>
                            <th>Data</th>
                            <th class="col-num">Cifră</th>
                            <th class="col-num">Marjă val.</th>
                            <th class="col-num">Marjă %</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="doc in agent.FacturiFaraAviz" :key="doc.DocumentId + '-fara'">
                            <td class="col-numar">
                              {{ doc.Numar || '—' }}
                              <span v-if="doc.LivrataPartial" class="badge-partial">livrat parțial</span>
                              <span v-if="doc.AreLiniiStorno" class="badge-storno-linii">linii storno</span>
                            </td>
                            <td class="col-data">{{ formatData(doc.Data) }}</td>
                            <td class="col-num">{{ formatValoare(doc.Cifra) }}</td>
                            <td class="col-num" :style="{ color: getMarjaColor(doc.MarjaProcentaj) }">
                              {{ formatValoare(doc.MarjaValoare) }}
                            </td>
                            <td class="col-num">
                              <v-chip size="x-small" variant="tonal"
                                :color="doc.MarjaProcentaj >= 20 ? 'green' : doc.MarjaProcentaj >= 10 ? 'orange' : 'red'">
                                {{ doc.MarjaProcentaj.toFixed(1) }}%
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- 3. Facturi storno -->
                  <div v-if="agent.FacturiStorno?.length" class="doc-group">
                    <div class="doc-group-header doc-group-header--storno">
                      <v-icon size="13" class="mr-1">mdi-swap-horizontal</v-icon>
                      Facturi storno
                      <span class="doc-group-count">{{ agent.FacturiStorno.length }}</span>
                    </div>
                    <div class="detalii-scroll">
                      <table class="detalii-table">
                        <thead>
                          <tr>
                            <th>Nr. Document</th>
                            <th>Data</th>
                            <th class="col-num">Cifră</th>
                            <th class="col-num">Marjă val.</th>
                            <th class="col-num">Marjă %</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="doc in agent.FacturiStorno" :key="doc.DocumentId">
                            <td class="col-numar">{{ doc.Numar || '—' }}</td>
                            <td class="col-data">{{ formatData(doc.Data) }}</td>
                            <td class="col-num col-storno">{{ formatValoare(doc.Cifra) }}</td>
                            <td class="col-num col-storno">{{ formatValoare(doc.MarjaValoare) }}</td>
                            <td class="col-num">
                              <v-chip size="x-small" variant="tonal" color="red">
                                {{ doc.MarjaProcentaj.toFixed(1) }}%
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                </template>
              </div>
            </v-expand-transition>

          </v-card>
        </v-col>
      </v-row>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppSettings from '@/settings/appSettings'
import COM_RaportVanzari from '@/services/Comercial/COM_RaportVanzari'

export default defineComponent({
  name: 'RaportVanzariAgenti',

  data() {
    return {
      APP_Color: AppSettings.APP_Color,
      loading: false,
      hasLoaded: false,
      raportData: [] as any[],

      anSelectat: new Date().getFullYear(),
      lunaSelectata: new Date().getMonth() + 1,

      ani: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i),
      luni: [
        { label: 'Ianuarie', value: 1 },
        { label: 'Februarie', value: 2 },
        { label: 'Martie', value: 3 },
        { label: 'Aprilie', value: 4 },
        { label: 'Mai', value: 5 },
        { label: 'Iunie', value: 6 },
        { label: 'Iulie', value: 7 },
        { label: 'August', value: 8 },
        { label: 'Septembrie', value: 9 },
        { label: 'Octombrie', value: 10 },
        { label: 'Noiembrie', value: 11 },
        { label: 'Decembrie', value: 12 },
      ],

      expandedId: null as string | null,
      searchAgent: '',

      menuItems: [
        { label: 'Dashboard', route: '/crm/dashboard', icon: 'mdi-view-dashboard' },
        { label: 'Parteneri', route: '/crm/parteneri', icon: 'mdi-account-tie' },
        { label: 'Oportunități', route: '/crm/grid-oportunitati', icon: 'mdi-briefcase' },
        { label: 'Activități', route: '/crm/activitati', icon: 'mdi-calendar-check' },
        { label: 'KPI', route: '/crm/kpi', icon: 'mdi-chart-bar' },
        { label: 'Raport Vânzări', route: '/crm/raport-vanzari', icon: 'mdi-chart-line' },
      ],
    }
  },

  computed: {
    lunaNume(): string {
      return this.luni.find(l => l.value === this.lunaSelectata)?.label ?? ''
    },
    totalCifra(): number {
      return this.raportData.reduce((s, r) => s + (r.Cifra ?? 0), 0)
    },
    totalMarjaValoare(): number {
      return this.raportData.reduce((s, r) => s + (r.MarjaValoare ?? 0), 0)
    },
    totalMarjaPct(): number {
      return this.totalCifra > 0 ? (this.totalMarjaValoare / this.totalCifra) * 100 : 0
    },
    raportDataFiltrat(): any[] {
      if (!this.searchAgent?.trim()) return this.raportData
      const q = this.searchAgent.trim().toLowerCase()
      return this.raportData.filter(a => a.AgentNume?.toLowerCase().includes(q) || a.AgentCod?.toLowerCase().includes(q))
    },
  },

  mounted() {
    this.incarcaRaport()
  },

  methods: {
    async incarcaRaport() {
      this.loading = true
      this.hasLoaded = false
      this.expandedId = null
      try {
        const res = await COM_RaportVanzari.getRaportAgenti(this.anSelectat, this.lunaSelectata)
        this.raportData = res.data ?? []
      } catch {
        this.raportData = []
      } finally {
        this.loading = false
        this.hasLoaded = true
      }
    },

    toggleDetalii(agent: any) {
      this.expandedId = this.expandedId === agent.AgentId ? null : agent.AgentId
    },

    getMarjaColor(pct: number): string {
      if (pct >= 20) return '#4CAF50'
      if (pct >= 10) return '#FF9800'
      return '#F44336'
    },

    marjaColorRGB(pct: number): string {
      if (pct >= 20) return '76,175,80'
      if (pct >= 10) return '255,152,0'
      return '244,67,54'
    },

    formatValoare(val: number): string {
      return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0)
    },

    formatData(val: string | null): string {
      if (!val) return '—'
      return new Date(val).toLocaleDateString('ro-RO')
    },

    isActiveRoute(route: string): boolean {
      return this.$route.path === route
    },
  },
})
</script>

<style scoped>
.raport-vanzari {
  padding: 2rem;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

/* ── Nav menu ── */
.nav-menu-card {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  overflow: hidden;
}

.nav-menu {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
  overflow-x: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  text-decoration: none;
  color: #718096;
  font-weight: 500;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-item:hover {
  background: #f7fafc;
  color: #1a202c;
}

.nav-item.active {
  background: linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(255, 87, 34, 0.05) 100%);
  color: var(--v-theme-primary);
  font-weight: 600;
}

/* ── Filter bar ── */
.filter-card {
  border-radius: 14px !important;
  padding: 1rem 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-label {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
}

.filter-select {
  max-width: 140px;
}

.refresh-btn {
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  height: 34px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: white !important;
  color: #4a5568 !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
  font-size: 0.8125rem !important;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.1) !important;
  background: rgba(255, 87, 34, 0.04) !important;
  color: #ff5722 !important;
  border-color: rgba(255, 87, 34, 0.3) !important;
}

.refresh-btn .v-icon {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.refresh-btn:hover .v-icon {
  transform: rotate(180deg);
}

/* ── Summary cards ── */
.summary-card {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
}

.summary-inner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1a202c;
  line-height: 1;
}

.summary-value--small {
  font-size: 1.1rem;
}

.summary-label {
  font-size: 0.75rem;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-top: 4px;
}

/* ── Agent card ── */
.agent-card {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.agent-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07) !important;
}

.agent-card--expanded {
  border-color: rgba(255, 87, 34, 0.2) !important;
  box-shadow: 0 4px 16px rgba(255, 87, 34, 0.08) !important;
}

.agent-card-inner {
  padding: 1.25rem;
  user-select: none;
}

.agent-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 4px;
  flex-wrap: wrap;
}

.expand-icon {
  transition: transform 0.25s ease;
}

.expand-icon--open {
  transform: rotate(180deg);
}

.agent-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.agent-cifra-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 0.6rem;
}

.agent-cifra-val {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1a202c;
}

.agent-cifra-label {
  font-size: 0.75rem;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.agent-pct {
  font-size: 0.875rem;
  font-weight: 700;
  min-width: 44px;
  text-align: right;
}

.agent-marja-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.agent-marja-label {
  font-size: 0.8125rem;
  color: #718096;
}

.agent-marja-val {
  font-size: 0.8125rem;
  font-weight: 600;
}

/* ── Detalii expand ── */
.agent-detalii {
  border-top: 1px solid #f0f0f0;
  padding: 0.75rem 1.25rem 1rem;
  background: #fafbfc;
  border-radius: 0 0 14px 14px;
}

.detalii-empty {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #a0aec0;
  padding: 0.5rem 0;
}

/* ── Document groups ── */
.doc-group {
  margin-bottom: 0.75rem;
}

.doc-group:last-child {
  margin-bottom: 0;
}

.doc-group-header {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.3rem 0.6rem;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
}

.doc-group-count {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: 800;
  opacity: 0.8;
}

.doc-group-header--aviz {
  background: rgba(33, 150, 243, 0.08);
  color: #1565C0;
  border-left: 3px solid #2196F3;
}

.doc-group-header--fara-aviz {
  background: rgba(156, 39, 176, 0.08);
  color: #6A1B9A;
  border-left: 3px solid #9C27B0;
}

.doc-group-header--storno {
  background: rgba(244, 67, 54, 0.08);
  color: #B71C1C;
  border-left: 3px solid #F44336;
}

.detalii-scroll {
  max-height: 200px;
  overflow-y: auto;
  border-radius: 0 0 6px 6px;
  border: 1px solid #edf2f7;
  border-top: none;
}

.detalii-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  background: white;
}

.detalii-table thead th {
  background: #f7fafc;
  padding: 0.4rem 0.65rem;
  text-align: left;
  font-size: 0.68rem;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #edf2f7;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.detalii-table thead th.col-num {
  text-align: right;
}

.detalii-table tbody td {
  padding: 0.35rem 0.65rem;
  border-bottom: 1px solid #f7fafc;
  color: #2d3748;
  vertical-align: middle;
}

.detalii-table tbody tr:last-child td {
  border-bottom: none;
}

.detalii-table tbody tr:hover td {
  background: #f7fafc;
}

.col-numar {
  font-weight: 500;
}

.col-data {
  color: #a0aec0;
  white-space: nowrap;
  font-size: 0.75rem;
}

.col-num {
  text-align: right;
  white-space: nowrap;
}

.col-storno {
  color: #F44336;
}

/* Badge-uri facturi */
.badge-storno-linii {
  display: inline-block;
  margin-left: 5px;
  padding: 1px 6px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(244, 67, 54, 0.1);
  color: #B71C1C;
  border-radius: 4px;
  border: 1px solid rgba(244, 67, 54, 0.3);
  vertical-align: middle;
}

.badge-partial {
  display: inline-block;
  margin-left: 5px;
  padding: 1px 6px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(255, 152, 0, 0.12);
  color: #E65100;
  border-radius: 4px;
  border: 1px solid rgba(255, 152, 0, 0.3);
  vertical-align: middle;
}

@media (max-width: 600px) {
  .raport-vanzari {
    padding: 1rem;
  }

  .filter-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
